import { components } from '@feedfm/clips-services-api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollectionCardWrapper } from '../../../../../components/CollectionCard/CollectionCard';
import { useStudioAuthenticatedContext } from '../../../../../context/StudioContext';
import * as styles from './DashboardCollectionSection.styles';

type CollectionAndArtwork = components['schemas']['CollectionAndArtwork'];
type CollectionSummaryRow =
  components['schemas']['CollectionsResponse']['collections'][number];

const RECENT_COLLECTION_COUNT = 3;

function sortResponseByPublishedOn<
  T extends CollectionSummaryRow | CollectionAndArtwork,
>(a: T, b: T): number {
  // A collection extends summary, so we can treat both as a summary
  const aSummary = 'summary' in a ? a.summary : a.collection;
  const aDate = aSummary.publishedOn
    ? new Date(aSummary.publishedOn).getTime()
    : 0;
  const bSummary = 'summary' in b ? b.summary : b.collection;
  const bDate = bSummary.publishedOn
    ? new Date(bSummary.publishedOn).getTime()
    : 0;

  if (aDate === 0) return 1;
  if (bDate === 0) return -1;

  return bDate - aDate;
}

function pickRecentSummaries(
  responses: CollectionSummaryRow[],
): CollectionSummaryRow[];
function pickRecentSummaries(
  responses: CollectionAndArtwork[],
): CollectionAndArtwork[];
function pickRecentSummaries(
  responses: (CollectionSummaryRow | CollectionAndArtwork)[],
): (CollectionSummaryRow | CollectionAndArtwork)[] {
  const sorted = [...responses];

  sorted.sort(sortResponseByPublishedOn);

  const result = sorted
    .slice(0, RECENT_COLLECTION_COUNT)
    .map<CollectionSummaryRow | CollectionAndArtwork>((response) =>
      'summary' in response
        ? {
            summary: response.summary,
            artworkPreSignedUrls: response.artworkPreSignedUrls,
            thumbnailUrl: response.thumbnailUrl,
          }
        : {
            collection: response.collection,
            artworkPreSignedUrls: response.artworkPreSignedUrls,
            thumbnailUrl: response.thumbnailUrl,
          },
    );

  return result;
}

export const DashboardCollectionSection = () => {
  const navigate = useNavigate();
  const { backendClient } = useStudioAuthenticatedContext();
  const [summaries, setSummaries] = useState<CollectionSummaryRow[] | null>(
    null,
  );
  const [details, setDetails] = useState<CollectionAndArtwork[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummaries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await backendClient.collections();
      setSummaries(response.collections);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred while fetching collections',
      );
    } finally {
      setLoading(false);
    }
  }, [backendClient]);

  useEffect(() => {
    loadSummaries();
  }, [loadSummaries]);

  const recentSummaries = useMemo(
    () => (summaries === null ? [] : pickRecentSummaries(summaries)),
    [summaries],
  );

  const loadDetails = useCallback(async () => {
    try {
      setDetails(null);

      const loaded = await Promise.all(
        recentSummaries.map((row) =>
          backendClient.collectionById(row.summary.collectionId),
        ),
      );
      setDetails(loaded);
    } catch (err) {
      setDetails(null);
    }
  }, [recentSummaries, backendClient]);

  // Load details after summaries are in
  useEffect(() => {
    if (summaries === null) {
      return;
    }

    loadDetails();
  }, [summaries, recentSummaries, backendClient]);

  const recentDetails = useMemo(
    () => (details === null ? null : pickRecentSummaries(details)),
    [details],
  );

  if (loading) {
    return (
      <Box>
        <Typography
          variant="h6"
          color="text.primary"
          sx={styles.titleTypography}
        >
          New
        </Typography>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography
          variant="h6"
          color="text.primary"
          sx={styles.titleTypography}
        >
          New
        </Typography>
        <Typography color="error" sx={styles.errorTypography}>
          {error}
        </Typography>
      </Box>
    );
  }

  // We will render details if they're loaded, otherwise just the summaries. The underlying card
  // component gracefully enhances based on what's available.
  const collections = recentDetails || recentSummaries;

  return (
    <Box>
      <Box sx={styles.headerContainer}>
        <Typography
          variant="h6"
          color="text.primary"
          sx={styles.titleTypography}
        >
          New
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/collections')}
          sx={styles.browseButton}
        >
          Browse all
        </Button>
      </Box>

      {recentSummaries.length === 0 ? (
        <Typography color="text.secondary" sx={styles.emptyStateTypography}>
          No collections available yet.
        </Typography>
      ) : (
        <Box sx={styles.collectionsContainer}>
          {collections.map((collection, index) => (
            <Box key={index} sx={styles.collectionCardContainer}>
              <CollectionCardWrapper collection={collection} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
